import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

interface SnackbarContextType {
  showAlert: (message: string, severity?: AlertColor) => void;
  hideAlert: () => void;
}

const defaultState: SnackbarContextType = {
  showAlert: () => {},
  hideAlert: () => {}
};

export const SnackbarContext = createContext<SnackbarContextType>(defaultState);

export const useSnackbar = () => useContext(SnackbarContext);

interface SnackbarProviderProps {
  children: ReactNode;
}

interface AlertState {
  open: boolean;
  message: string;
  severity: AlertColor;
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
  const [alert, setAlert] = useState<AlertState>({ open: false, message: '', severity: 'info' });

  const showAlert = useCallback((message: string, severity: AlertColor = 'info') => {
    setAlert({ open: true, message, severity });
  }, []);

  const hideAlert = useCallback(() => {
    setAlert((prev) => ({ ...prev, open: false }));
  }, []);

  return (
    <SnackbarContext.Provider value={{ showAlert, hideAlert }}>
      {children}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={hideAlert}
      >
        <Alert onClose={hideAlert} severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};